import React from 'react'
import { CampaignGoalProps } from './CampaignGoal.types'
import { ActionCard } from 'components/cards/action-card'
import { useGetBusinessID } from '@marketing-milk/frontend'
import Select from 'react-select'
import {
  campaignObjectives,
  OutcomeCampaignObjective,
  optimizationGoalsForObjective,
  PixelEventType,
} from '@marketing-milk/interfaces'
import { useCustomConversions } from '../../../../../../hooks/campaigns/useCustomConversionsByBusiness'
import { capitalCase } from 'change-case'

// Sentinels rather than events anyone can optimize toward: the API returns them, but neither
// names something a pixel fires.
const unselectablePixelEvents = new Set([PixelEventType.FACEBOOK_SELECTED, PixelEventType.OTHER])

export function CampaignGoal({
  optimization,
  onSelectOptimization,
  pixelEventOrCCID,
  isLiveOnFacebook,
  onCustomizeGoalConfig,
}: CampaignGoalProps) {
  const businessID = useGetBusinessID()
  const { data: customConversions } = useCustomConversions(Number(businessID))

  const selectOptimization = (objective: OutcomeCampaignObjective) => {
    if (isLiveOnFacebook) return
    const goals = optimizationGoalsForObjective(objective)
    // the first entry is the one the team publishes most under that objective
    if (goals.length) onSelectOptimization(goals[0])
  }

  const performanceGoalOptions = optimization
    ? optimizationGoalsForObjective(optimization.objective).map(entry => ({
        label: entry.label,
        value: entry.key,
        entry,
      }))
    : []

  const pixelEventOptions = Object.values(PixelEventType)
    .filter(pixelEventType => !unselectablePixelEvents.has(pixelEventType))
    .map(pixelEventType => ({
      label: capitalCase(pixelEventType.toLowerCase().replaceAll('_', ' ')),
      value: pixelEventType as PixelEventType | number,
    }))
  const customConversionOptions =
    customConversions?.map(({ name, id, facebookCCIdentifier }) => ({
      label: `${name} (ID: ${facebookCCIdentifier})`,
      value: id as PixelEventType | number,
    })) ?? []

  return (
    <div className="w-full" data-testid="campaign-optimizations">
      <div className="w-full">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-mm-neutral-1-700">
            Choose Your Campaign Objective
          </h3>
          <p className="text-sm text-mm-neutral-1-600">
            What Meta should deliver this campaign for.
          </p>
          {!optimization && (
            <div
              className="text-warning bg-warning faded px-3 py-2 rounded mt-2"
              data-testid="no-optimization-selected"
            >
              Nothing is selected yet. Pick an objective below — this campaign can't be published,
              and its creatives can't be filtered, until you do.
            </div>
          )}
          {isLiveOnFacebook && (
            <div className="text-warning bg-warning faded px-3 py-2 rounded mt-2">
              This campaign is already live on Facebook, so its delivery settings are locked.
              Duplicate the campaign to change delivery.
            </div>
          )}
        </div>
        {/* auto-fill rather than a breakpoint count: this screen renders inside an 80vw drawer
            as well as full width, and a viewport breakpoint keeps columns that don't fit. */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-4">
          {campaignObjectives.map(objectiveOption => {
            const selected = optimization?.objective === objectiveOption.objective
            return (
              <ActionCard
                key={objectiveOption.objective}
                classes={`h-full text-center ${isLiveOnFacebook ? 'cursor-default' : ''} ${
                  selected ? 'selected' : ''
                }`}
                active={selected}
                onClick={() => selectOptimization(objectiveOption.objective)}
              >
                <div className={`p-2 ${isLiveOnFacebook ? 'text-inactive' : ''}`}>
                  <i className={`fad fa-${objectiveOption.icon} fa-2x mb-0 text-mm-primary-500`} />
                  <h4 className="mt-3 text-base font-semibold text-mm-neutral-1-900">
                    {objectiveOption.label}
                  </h4>
                  <p className="mt-1 text-sm text-mm-neutral-1-600">
                    {objectiveOption.description}
                  </p>
                </div>
              </ActionCard>
            )
          })}
        </div>

        {!!optimization && (
          <>
            <div className="w-full grid sm:grid-cols-3 gap-4 py-4 mt-6 border-t border-mm-neutral-1-200 items-center">
              <div>
                <p className="text-sm font-semibold text-mm-neutral-1-700">Performance Goal</p>
                <p className="mt-1 text-sm text-mm-neutral-1-600">{optimization.description}</p>
              </div>
              <div className="sm:col-span-2">
                <Select
                  isDisabled={isLiveOnFacebook}
                  placeholder="Select what to optimize delivery for"
                  value={performanceGoalOptions.find(_ => _.value === optimization.key)}
                  options={performanceGoalOptions}
                  onChange={(e: any) => !!e?.entry && onSelectOptimization(e.entry)}
                />
              </div>
            </div>
          </>
        )}

        {optimization?.promotedObjectKind === 'web-event' && (
          <>
            <div className="w-full grid sm:grid-cols-3 gap-4 py-4 border-t border-mm-neutral-1-200 items-center">
              <p className="text-sm font-semibold text-mm-neutral-1-700">Conversion Event</p>
              <div className="sm:col-span-2">
                <Select
                  isDisabled={isLiveOnFacebook}
                  placeholder="Select Web Event to optimize for"
                  value={
                    pixelEventOptions.find(_ => _.value === pixelEventOrCCID) ??
                    customConversionOptions.find(_ => _.value === pixelEventOrCCID)
                  }
                  options={[
                    {
                      label: 'Custom Conversions',
                      options: customConversionOptions,
                    },
                    {
                      label: 'Pixel Events',
                      options: pixelEventOptions,
                    },
                  ]}
                  onChange={(e: any) =>
                    !!e?.value && !!onCustomizeGoalConfig && onCustomizeGoalConfig(e.value)
                  }
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
